import React, { useContext, useState } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import { colors } from '@roku-web-ui/rds-theme';
import ReactPlayer from 'react-player/file';

import { ConfigContext } from 'context/config';

import { IconDownload } from 'components/icons';
import { Tooltip } from 'components/common/Tooltip';

import { useAspenLogger } from 'hooks/useAspenLogger';

import { DownloadTools } from 'modules/download';

interface DownloadProps {
  source: string;
  deviceName?: string;
  timestamp?: string | number;
  playerRef?: React.RefObject<ReactPlayer>;
  rotation?: number;
  disabled?: boolean;
}

export const Download = ({
  source,
  deviceName,
  timestamp,
  playerRef: _playerRef,
  rotation = 0,
  disabled = false,
}: DownloadProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(-1); // Start with indeterminate
  const { logAspenEvent } = useAspenLogger();
  const { baseUrl, csrfToken } = useContext(ConfigContext);

  const isDashOrHlsUrl = (url: string): boolean => {
    const lowerUrl = url.toLowerCase();
    return (
      lowerUrl.includes('.mpd') ||
      lowerUrl.includes('.m3u8') ||
      lowerUrl.includes('getdashmanifest') ||
      lowerUrl.includes('gethlsmanifest') ||
      lowerUrl.includes('kinesisvideo')
    );
  };

  const handleDownload = async () => {
    if (!source || isDownloading || disabled) return;

    setIsDownloading(true);
    setProgress(-1); // Start with indeterminate

    try {
      // All videos are cross-origin - use server proxy for reliable downloads
      const downloadTools = DownloadTools({ baseUrl, csrfToken });
      const result = await downloadTools.downloadFile({
        url: source,
        deviceName,
        timestamp,
        rotation,
        onProgress: setProgress,
      });

      if (!result.success) {
        // Don't log user cancellation as an error
        if (result.error !== 'Download cancelled by user') {
          logAspenEvent({
            log_id: 'iot.web.event.video.download.error',
            payload: {
              device_name: deviceName,
              error: result.error || 'Unknown error',
            },
          });
        }
      } else {
        // Server will choose the best method based on format and rotation needs
        let downloadMethod;
        if (isDashOrHlsUrl(source)) {
          downloadMethod = rotation > 0 ? 'server_ffmpeg_rotation' : 'server_dash_lightweight';
        } else {
          downloadMethod = 'server_proxy';
        }

        logAspenEvent({
          log_id: 'iot.web.event.video.download',
          payload: {
            device_name: deviceName,
            download_method: downloadMethod,
            rotation_degrees: rotation,
          },
        });
      }
    } catch (error) {
      logAspenEvent({
        log_id: 'iot.web.event.video.download.error',
        payload: {
          device_name: deviceName,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });
    } finally {
      setIsDownloading(false);
      setProgress(-1); // Reset to indeterminate
    }
  };

  const tooltipTitle = (() => {
    if (disabled) return 'Video is being processed';
    if (!isDownloading) return 'Download video';
    if (progress >= 0) return `Downloading... ${progress}%`;
    return 'Downloading...';
  })();

  return (
    <Tooltip title={tooltipTitle} placement="top">
      <div>
        <IconButton
          aria-label="download"
          data-testid="button-download"
          disabled={!source || isDownloading || disabled}
          onClick={handleDownload}
          sx={{
            '&.MuiIconButton-root': {
              display: 'inline-flex',
              padding: '0', // Override RDS padding
              position: 'relative',
            },
            '&:disabled': {
              opacity: 0.5,
              pointerEvents: isDownloading ? 'auto' : 'none', // Allow tooltip during download
            },
            '&:hover': {
              boxShadow: 'none',
            },
          }}
        >
          {isDownloading ? (
            <CircularProgress
              size={24}
              variant={progress >= 0 ? 'determinate' : 'indeterminate'}
              value={progress >= 0 ? progress : undefined}
              sx={{ color: colors.grey[400] }}
            />
          ) : (
            <IconDownload sx={{ color: colors.grey[400] }} />
          )}
        </IconButton>
      </div>
    </Tooltip>
  );
};
